import { DOCUMENT, NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  type ElementRef,
  EventEmitter,
  NgZone,
  Output,
  afterNextRender,
  computed,
  effect,
  inject,
  signal,
  untracked,
  viewChild,
} from '@angular/core';

import { takeUntil } from 'rxjs/operators';

import { isHorizontalType } from '@23degrees/shared-code/config/constants';
import { isArray, uuid } from '@23degrees/shared-code/utilities';
import { MessageBus } from '@23degrees/shared/bus';
import { EventBus } from '@23degrees/shared/bus/event/event.bus';
import { ResizeObserverDirective } from '@23degrees/shared/directives';
import { ChartManager } from '@23degrees/shared/manager/chart/chart.manager';
import { TrackerService } from '@23degrees/shared/services/tracker/tracker.service';

import { EDITOR_MESSAGES, MESSAGE_RECIPIENTS } from '@config';

import { LayoutElementComponent } from '../layout-element/layout-element.component';

export const isClose = (value: number, target: number, tolerance: number): boolean => {
  if (!Number.isFinite(value) || !Number.isFinite(target) || !Number.isFinite(tolerance)) {
    return false;
  }

  return Math.abs(value - target) <= tolerance;
};

@Component({
  selector: 'layout-chart',
  templateUrl: './layout-chart.component.html',
  styleUrls: ['./layout-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgClass, ResizeObserverDirective],
})
export class LayoutChartComponent extends LayoutElementComponent {
  private document = inject<Document>(DOCUMENT);
  private eventBus = inject(EventBus);
  private messageBus = inject(MessageBus);
  private chartManager = inject(ChartManager, { optional: true });
  private trackerService = inject(TrackerService);
  private zone = inject(NgZone);

  @Output() chartEvent = new EventEmitter<any>();

  public chartDiv = viewChild<ElementRef<HTMLDivElement>>('chart');

  // content / data derives
  public noData = computed(() => {
    const data = this.config().data;
    return isArray(data) ? data.length === 0 : true;
  });

  public dynamicHeight = computed(() => isHorizontalType(this.content().type) && this.content()?.typeSpecific.chart.dynamicHeight);

  public hideScrollbar = computed(() => !this.hasOverflow() || (this.dynamicHeight() && this.content()?.localStorage.hideScrollbar));

  public noSeries = signal(false);
  public loadedView = signal(false);
  public hasOverflow = signal(false);

  private prevChartWidth: number;
  private prevChartHeight: number;

  private containerCreated = false;
  private chartCreated = false;

  readonly chartHash = signal('');

  constructor() {
    super();

    afterNextRender({
      // write: () => {
      //   console.log('write', !!this.chartManager);
      //   if (this.chartManager && !this.containerCreated) {
      //     this.chartManager.createChartContainer(this.chartDiv().nativeElement);
      //     this.containerCreated = true;
      //   }
      // },
      mixedReadWrite: () => {
        // console.log('chart afterNextRender');
        // console.log('mixedReadWrite', !!this.chartManager);
        if (this.chartManager && !this.containerCreated) {
          this.chartManager.createChartContainer(this.chartDiv().nativeElement);
          this.containerCreated = true;
        }

        this.emitRenderStatus('chartReady');
      },
    });

    effect(() => {
      const content = this.content();
      const layoutReady = this.context().ready();

      if (layoutReady) {
        // this.chartHash.set(uuid());
        untracked(() => {
          this.chartCreated = true;
          this.updateChartSize(true, 'layoutReadySignal');
        });
      }
    });
  }

  ngOnInit() {
    if (!this.chartManager) return;

    this.chartManager.getAction.pipe(takeUntil(this.componentDestroyed$)).subscribe((action) => {
      if (action.eventType === 'animationStart') {
        this.loadedView.set(false);
        this.messageBus.emit('RENDER_START', [MESSAGE_RECIPIENTS.ContentSelector], {}, this.config().referenceID);
      }

      if (action.eventType === 'animationDone') {
        this.loadedView.set(true);
        this.messageBus.emit('RENDER_FINISH', [MESSAGE_RECIPIENTS.ContentSelector, MESSAGE_RECIPIENTS.Filter], {}, this.config().referenceID);
      }

      if (action.eventType === 'isValid') {
        this.noSeries.set(!action.item);
        // console.log('[LayoutChartComponent] noSeries', this.noSeries);
      }
    });

    this.messageBus.on('LAYOUT_READY_FOR_CHART', MESSAGE_RECIPIENTS.Chart, this.receiveLayoutReady, this.componentDestroyed$, this.config().referenceID);
  }

  ngOnDestroy() {
    if (this.chartManager) {
      this.chartManager.destroyChartContainer();
    }

    super.ngOnDestroy();
  }

  private receiveLayoutReady = (msg) => {
    // this.isLayoutReadyForChart = true;
    // this.updateChartSize(true, 'receiveLayoutReady');
  };

  public resizeChart = (event: ResizeObserverEntry) => {
    this.updateChartSize(false, 'resizeChart');
  };

  public handleScroll = (event) => {
    this.messageBus.emit('TOOLTIP_CLOSE', [MESSAGE_RECIPIENTS.ToolTip], {}, this.config().referenceID);
  };

  private updateChartSize = (force: boolean, who: string, noResize = false) => {
    if (!this.config()) return;
    if (!this.chartManager) return;
    if (!this.containerCreated) return;

    const { offsetWidth, offsetHeight, scrollWidth, scrollHeight } = this.chartDiv().nativeElement;
    // console.log('updateChartSize 0', who, force, offsetWidth, offsetHeight);

    if (!this.dynamicHeight() && (offsetWidth === 0 || offsetHeight === 0)) return;
    if (Number.isNaN(offsetWidth) || Number.isNaN(offsetHeight)) return;

    const { content, data, range } = this.config();
    if (!content || !data || !range) return;

    const sizeTolerance = 1.5;
    const sizeChanged = !isClose(offsetWidth, this.prevChartWidth, sizeTolerance) || !isClose(offsetHeight, this.prevChartHeight, sizeTolerance);
    // console.log('updateChartSize 1', who, sizeChanged, offsetWidth, offsetHeight);

    if (force || sizeChanged) {
      this.prevChartWidth = offsetWidth;
      this.prevChartHeight = offsetHeight;

      // console.log('updateChartSize 2', who, offsetWidth, offsetHeight);

      this.chartManager.updateContentData(content, data, range, offsetWidth);

      if (this.context().ready()) {
        this.zone.runOutsideAngular(() => {
          const hasOverflow = this.chartManager.updateChartContainerSize(offsetWidth, offsetHeight);
          this.chartManager.updateChart();

          this.hasOverflow.set(hasOverflow);
        });
      }

      // console.log('>>>', 'chart updateChartSize', who, `${Math.floor(offsetWidth)}x${Math.floor(offsetHeight)}`, offsetHeight, this.prevChartHeight);
    }

    // Dynamic Height
    if (this.dynamicHeight()) {
      const slug = content.slug;
      const { referenceID, blockPostMessages } = content.localStorage;
      const contentSelector = this.document.querySelector(`#cs-${referenceID}`);

      if (contentSelector) {
        const deltaHeight = this.chartManager.totalHeight - offsetHeight;
        const dynamicHeight = contentSelector.clientHeight + deltaHeight;

        try {
          if (Number.isFinite(dynamicHeight)) {
            this.messageBus.emit(EDITOR_MESSAGES.DisplayUpdate, [MESSAGE_RECIPIENTS.Editor], { height: dynamicHeight, slug: slug });
            this.eventBus.dynamicHeight.next({ context: { height: dynamicHeight, referenceID: referenceID }, slug: slug });

            if (!blockPostMessages && window?.parent) {
              const location = this.document.location.href;
              this.trackerService.postMessage({ sentinel: 'amp', type: 'embed-size', height: dynamicHeight });
              this.trackerService.postMessage({ action: 'dynamicSize', height: dynamicHeight, contentSlug: slug, location: location });

              // tell content-selector about dynamic height
              content.localStorage.dynamicHeightAMP = true;
            }
          }
        } catch (error) {
          console.log('[LayoutChart] postMessage error:', error);
        }
      }
    }
  };
}
